import { Button, Checkbox, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
  FieldSet,
  IDataTableColumn,
  useGridState,
  useSearchApi,
  ButtonContainer,
  showSuccess,
  showError,
} from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import {
  DogApi,
  DogsTransientTitlesSearch,
  ErrorDetails,
  ESRSHExceptions,
  TransientDogTitleGridDto,
  TransientDogTitlesApi,
} from "../../../api";
import { ERoutes } from "../../../ERoutes";
import { CustomDataTable, ExportButton, SharepointButton } from "../../../components";
import { useGlobalData, useDialog } from "../../../contexts";
import { useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import styled from "styled-components";

export interface IDogTransientTitlesProps {
  searchUnassignedTitles: boolean;
}

const StyledCheckbox = styled(Checkbox)`
  font-size: 10pt;
  top: 6px;
`;

export const DogTransientTitlesList: React.FunctionComponent<IDogTransientTitlesProps> = ({
  searchUnassignedTitles,
}) => {
  const api = useApiService(TransientDogTitlesApi);
  const dogApi = useApiService(DogApi);
  const { t, tUnsafe } = useTl();
  const { currentLanguage } = useGlobalData();
  const { showDialogPromise } = useDialog();

  const [saving, setsaving] = React.useState<boolean>();

  const [sObjState, setsObjState] = React.useState<any>();
  const [selectionList, setSelectionlist] = React.useState<number[]>([]);
  const [selectAll, setselectAll] = React.useState(false);
  const [hideClosed, sethideClosed] = React.useState(true);

  const tableState = useGridState<any>({
    serverMode: true,
    availablePageSizes: [10],
    enablePagination: true,
    enableFilter: true,
    sortKeys: { dog: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DogsTransientTitlesSearch) => {
      const s = DogsTransientTitlesSearch.fromJS({
        ...sObj,
        hideClosed: hideClosed,
        searchUnassignedTitles: searchUnassignedTitles,
        languageId: currentLanguage.languageId,
      });
      setsObjState(s);
      return api.baseSearch(s);
    },
    [api, currentLanguage.languageId, hideClosed, searchUnassignedTitles]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const editableIdsList = React.useMemo(() => {
    var res = [];
    tableState.data?.map((row) => {
      if (!row.closedOn && !res.find((c) => c === row.dogTitlesTransientId)) {
        res.push(row.dogTitlesTransientId);
      }
    });
    return res;
  }, [tableState?.data]);

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...editableIdsList);
    } else {
      selectionList.splice(0, selectionList.length);
    }
    setSelectionlist([...selectionList]);
    setselectAll(value);
  }, [editableIdsList, selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: TransientDogTitleGridDto) => {
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.dogTitlesTransientId) && !row.closedOn}
          onChange={() => {
            if (selectionList.includes(row.dogTitlesTransientId)) {
              const index = selectionList.findIndex((e) => e === row.dogTitlesTransientId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.dogTitlesTransientId);
            }
            setSelectionlist([...selectionList]);
          }}
          disabled={!!row.closedOn}
        />
      );
    },
    [editableIdsList, selectionList]
  );
  const [shpLoading, setShpLoading] = React.useState(null);
  const openOrCreateInShp = React.useCallback(
    async (data: TransientDogTitleGridDto) => {
      setShpLoading(data?.dogId);
      if (!!data?.sharepointUri) {
        window.open(data?.sharepointUri, "_blank");
        setShpLoading(null);
      } else {
        try {
          await dogApi.createInSharepoint(data?.dogId);
          search();
        } catch (e) {
          throw e;
        } finally {
          setShpLoading(null);
        }
      }
    },
    [dogApi, search]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox checked={selectAll} onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        header: () => t(ETLCodes.Dog),
        fieldName: "dog",
        render: (row: TransientDogTitleGridDto) => {
          return (
            <Link target="_blank" to={`${ERoutes.dog}/${row.dogId}/detail/view`}>
              {row.dog}
            </Link>
          );
        },
      },
      {
        header: () => t(ETLCodes.Title),
        fieldName: "title",
      },
      {
        header: () => t(ETLCodes.Country),
        fieldName: "country",
      },
      {
        header: () => t(ETLCodes.Year),
        fieldName: "year",
      },
      {
        header: () => t(ETLCodes.SharePoint),
        fieldName: "_action",
        render: (row: TransientDogTitleGridDto) => (
          <SharepointButton
            existsInShp={!!row?.sharepointUri}
            loading={shpLoading === row?.dogId}
            onClick={() => openOrCreateInShp(row)}
          />
        ),
      },
    ],
    [columnCheckBox, openOrCreateInShp, selectAll, shpLoading, t, toggleSelectAll]
  );

  const action = React.useCallback(async () => {
    setsaving(true);
    if (selectionList.length === 0) {
      await showDialogPromise({
        message: t(ETLCodes.SelectionEmpty),
        title: t(ETLCodes.SelectionEmpty),
        buttons: [
          {
            resultType: "yes",
            text: t(ETLCodes.General_Ok),
            intent: Intent.PRIMARY,
          },
        ],
      });
    } else {
      if (searchUnassignedTitles) {
        const res = await showDialogPromise({
          message: t(ETLCodes.AssignElementsQuestion),
          title: t(ETLCodes.Assign),
        });
        if (res === "ok" || res === "yes") {
          try {
            await api.assign(selectionList);
            search();
            showSuccess(t(ETLCodes.Success));
          } catch {
            showError(t(ETLCodes.Error));
          }
        }
      } else {
        const res = await showDialogPromise({
          message: t(ETLCodes.ValidateElementsQuestion),
          title: t(ETLCodes.Validate),
        });
        if (res === "ok" || res === "yes") {
          try {
            await api.validate(selectionList);
            search();
            showSuccess(t(ETLCodes.Success));
          } catch (error) {
            const details: ErrorDetails = JSON.parse(error.response);
            if (details?.errorCode) {
              if (details.errorCode === ESRSHExceptions.UniqueKeyException) {
                showError(t(ETLCodes.AtLeastOnOfTheTitlesAlreadyExists));
              } else {
                showError(tUnsafe(`BackendError_${details.errorCode}`));
              }
            } else {
              showError(t(ETLCodes.Error));
            }
          }
        }
      }
    }
    setSelectionlist([]);
    setselectAll(false);
    setsaving(false);
    search();
  }, [api, search, searchUnassignedTitles, selectionList, showDialogPromise, t, tUnsafe]);

  const refuse = React.useCallback(
    async (requestList: number[]) => {
      setsaving(true);
      if (requestList.length === 0) {
        await showDialogPromise({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
        });
      } else {
        const res = await showDialogPromise({
          message: t(ETLCodes.CloseTaskConfirmation),
          title: t(ETLCodes.Close),
        });
        if (res === "ok" || res === "yes") {
          await api.close(requestList);
          showSuccess(t(ETLCodes.SaveSuccess));
          search();
        }
      }
      setsaving(false);
    },
    [api, search, showDialogPromise, t]
  );

  return (
    <FieldSet
      title={searchUnassignedTitles ? t(ETLCodes.ForeignTitlesToAssign) : t(ETLCodes.ForeignTitlesToAValidate)}
      rightElement={
        <ButtonContainer>
          <Button
            text={searchUnassignedTitles ? t(ETLCodes.Assign) : t(ETLCodes.Validate)}
            onClick={() => action()}
            intent={Intent.PRIMARY}
            minimal={true}
            icon={IconNames.TICK_CIRCLE}
            loading={saving}
          />
          {!searchUnassignedTitles && (
            <Button
              text={t(ETLCodes.Refuse)}
              onClick={() => refuse(selectionList)}
              intent={Intent.PRIMARY}
              minimal={true}
              icon={IconNames.DELETE}
              loading={saving}
            />
          )}
          {!searchUnassignedTitles && (
            <StyledCheckbox
              checked={hideClosed}
              onClick={() => {
                sethideClosed(!hideClosed);
                search();
              }}
              label={t(ETLCodes.HideClosed)}
            />
          )}
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonContainer>
      }
    >
      <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
    </FieldSet>
  );
};
